import {Component, Input, OnDestroy, OnInit, ChangeDetectorRef, AfterViewInit, HostListener} from '@angular/core';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {FormGroup} from '@angular/forms';
import {ResolvableComponent} from '@ispm/core/decorators/resolvable-component.decorator';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import * as _ from 'lodash';
import {PlacementNodeData} from "@ispm-client/modules/bpmn-modeler/models/placement.data";
import {ConfirmModalComponent} from "@ispm/layout/components/modals/confirm-modal/confirm-modal.component";
import {ConfirmModalLoaderPayload} from "@ispm/layout/classes/confirm-modal-loader-payload";
import {ModalTypes} from "@ispm/layout/components/modals/contants/modal-types.enum";
import {DynamicModalLoaderService} from "@ispm/layout/services/dynamic-modal-loader.service";
import {modalResponse} from "@ispm/layout/constants/modal-responses.enum";

const NODES_SELECTION_CONTAINER_HEIGHT: number = 500;
const NODES_CONTAINER_MAX_HEIGHT: number = 370;

@Component({
  selector: 'gen-trial-placement-node',
  templateUrl: './trial-placement-node.component.html',
  styleUrls: ['./trial-placement-node.component.scss']
})

@ResolvableComponent(null, 'TrialPlacementNode')
export class TrialPlacementNodesComponent implements OnInit, OnDestroy, AfterViewInit, IWidgetItem {
  public formItems: IdynamicFormItem[] = [];
  public _nodes: PlacementNodeData[] = [];
  public widgetFormInstance: FormGroup;

  constructor(protected modalLoader: DynamicModalLoaderService, private _changeDetector: ChangeDetectorRef) {
  }

  @Input()
  public set nodes(data: any[]) {
    this._nodes = data;
  }

  public get nodes() {
    return this._nodes;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.setNodesAreaHeight();
  }

  ngOnInit() {
    this.setNodesAreaHeight();
    if (this.widgetFormInstance?.value?.clinicalTrial) {
      const formData = this.widgetFormInstance?.value;
      this._nodes = formData.nodes ?? [];
      this.setSelectedNode(formData.id);
      this.widgetUpdateValueCallback({
        "action": "initiateData",
        "data": this._nodes
      });
    }
  }

  ngAfterViewInit() {
    if (this.widgetFormInstance?.value?.clinicalTrial &&
      this.widgetFormInstance?.value?.nodes &&
      this.widgetFormInstance?.value?.nodes.length > 2 &&
      this.widgetFormInstance?.value?.id) {
      setTimeout(() => {
        try {
          document.querySelectorAll('.trial-placement-node-card').forEach(obj => {
            if (obj.attributes['node-data-id'].value === this.widgetFormInstance?.value?.id) {
              obj.scrollIntoView({behavior: 'smooth', inline: 'start', block: 'start'});
              return;
            }
          });
        } catch (e) {
        }
      }, 50);
    }
  }

  ngOnDestroy(): void {
  }

  widgetUpdateValueCallback: (value) => void;
  private onDestroyCallbacks: Array<Function> = [];

  set widgetConfig(widgetConfig: IdynamicFormItem) {
    Object.assign(this, widgetConfig.widgetPayload);
  }

  set widgetData(widgetData: any) {
    if (widgetData && widgetData.length > 0 && !widgetData.action) {
      let isUpdated = false;
      widgetData.forEach(obj => {
        let match = _.find(this._nodes, (node) => {
          if (obj.nodeId && obj.nodeId === node.nodeId)
            return obj;
        });

        if (!match) {
          isUpdated = true;
          this._nodes = [{
            nodeId: obj.code,
            condition: "",
            selected: true,
            availability: 'true',
            removed: false
          }, ...this._nodes];
          this.setSelectedNode(obj.code);
        }
      });

      if (isUpdated) {
        this.widgetUpdateValueCallback({
          "action": "updateData",
          "data": this._nodes
        });
      }
    }
  }

  public updateNodes(event) {
    if (event.updated && event.updated.length > 0) {
      let form = event.form;
      let index = _.findIndex(this._nodes, {nodeId: form.nodeId});
      if (index > -1) {
        switch (event.updated[0]) {
          case 'condition': {
            this._nodes[index].condition = form.condition;
            break;
          }
          case 'availability': {
            this._nodes[index].availability = form.availability;
            break;
          }
        }
      }

      this.widgetUpdateValueCallback({
        "action": "updateData",
        "data": this._nodes
      });
    }
  }

  public removeNode(event, item) {
    event.preventDefault();
    event.stopPropagation();

    const content: string = "Are you sure you want to delete the selected node from the Trial Placement?\n\r" +
      "Deleting the node will permanently remove it from the trial placement. This cannot be undone";
    this.modalLoader.popupModal(ConfirmModalComponent, new ConfirmModalLoaderPayload(
        "Delete Node Placement",
        content,
        "Delete",
        "Cancel"
      ).setBackdropAllowed(false),
      ModalTypes.WARNING
    )
      .map(response => response.payload)
      .subscribe((response: modalResponse) => {
          switch (response) {
            case modalResponse.approve: {
              _.remove(this._nodes, (obj) => {
                return obj.nodeId == item.nodeId;
              });
              this.setSelectedNode(null);
              this.widgetUpdateValueCallback({
                "action": "itemRemoved",
                "data": {nodes: this._nodes, removedNode: item.nodeId}
              });
              break;
            }
          }
        },
        () => {
        },
        () => {
        });
  }

  public nodeCardSelected(nodeId) {
    let node: PlacementNodeData = _.find(this._nodes, (obj) => {
      return obj.nodeId === nodeId;
    });

    if (node && !node.selected) {
      this.setSelectedNode(nodeId);
      this.widgetUpdateValueCallback({
        "action": "itemSelected",
        "data": {code: nodeId}
      });
    }
  }

  private setSelectedNode(nodeId) {
    _.forEach(this._nodes, (obj) => {
      obj.selected = obj.nodeId === nodeId;
    });

    this._nodes = [...this._nodes];
  }

  private setNodesAreaHeight() {
    const selectEl = document.getElementById('nodesSelectionContainer');
    const listEl = document.getElementById('nodesListContainer');
    if (selectEl && listEl) {
      let zoom = Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100);
      let heightOffset = zoom === 100 ? 0 : selectEl.clientHeight - NODES_SELECTION_CONTAINER_HEIGHT;
      listEl.style.maxHeight = (NODES_CONTAINER_MAX_HEIGHT + heightOffset).toString() + "px";
    }
  }
}
